import React from 'react';
import { NextPage } from 'next';
import Head from 'components/Head';
import PageLayout from 'layouts/Website';
import TopText from 'components/Website/TopText';
import LandingHeroSection from 'components/Website/LandingHero';
import StatsSection from 'components/Website/StatsSection';
import FeaturedSection from 'components/Website/FeatureSection';
import AwardBlock from 'components/Website/AwardBlock';
import LandingQuote from 'components/Website/LandingQuote';
import CallOutSection from 'components/Website/CallOutSection';
import TradingSystemTabs from 'components/Website/TradingSystem';
import TopBlock from 'components/Website/DifferentiatorSection/top-block';
import TrackerItems from 'components/Website/SmartMoney';
import Testimonials from 'components/Website/Testimonials';
import PricingCardsComponent from 'components/Website/Pricing';
import PricingGrid from 'components/Website/PricingCards/PricingGrid';
import CallOutHeadingSection from 'components/Website/ToolKitSection/Callout';
import Discord from 'components/Website/Discord';
import EmailVisuals from 'components/Website/EmailVisuals';
import {
  TECHNICAL_ANALYSIS_SIGNUP_MONTHLY,
  TECHNICAL_ANALYSIS_SIGNUP_YEARLY,
  TECHNICAL_ANALYSIS_SIGNUP_QUARTERLY,
  PRICING_SECTION,
} from 'constants/pages';

const testimonials = [
  {
    name: 'Victor R',
    since: 'Member Since August 2021',
    rating: 5,
    body: 'The InsiderFinance Algo is amazing at spotting trends! The supporting oscillators are great for confirming the trends the Algo show. I never trade without these! Thanks InsiderFinance!!',
  },
  {
    name: 'Nick L',
    since: 'Member Since December 2022',
    rating: 5,
    body: 'I just tried the InsiderFinance indicators and can’t believe how easy to use they are! I’m new to technical analysis, but I already feel much more confident in finding good chart setups after taking the masterclass.',
  },
  {
    name: 'Santos A',
    since: 'Member Since March 2021',
    rating: 5,
    body: 'These indicators are a game changer. They’ve saved me from some disastrous trades and are easy to read. Plus they’ve been super accurate. Highly recommended.',
  },
  {
    name: 'Sahira G',
    since: 'Member Since March 2022',
    rating: 5,
    body: (
      <div>
        I just signed up for the indicators and am in love!! This is the tool I’ve been looking for
        to help read charts, thanks team!!
        <br />
        My favorite part is using the alerts to find good entries or monitor my open positions.
      </div>
    ),
  },
  {
    name: 'Adam F',
    since: 'Member Since March 2021',
    rating: 5,
    body: 'I must say so far these indicators have been as accurate as you said they would be and easier to use than others I’ve tried. I’m pleasantly surprised.',
  },

  {
    name: 'Phuc D',
    since: 'Member Since March 2021',
    rating: 5,
    body: "InsiderFinance has improved my profits from trading. It allows me to take positions with better winning odds. It also gives me confidence knowing that there is 'Smart Money' backing my trade. Highly recommend.",
  },
  {
    name: 'Wiro S',
    since: 'Member Since March 2020',
    rating: 5,
    body: 'InsiderFinance has increased my hit rate by a noticeable amount. It allows me to narrow down trades to ones with strong technical support. It also gives me confidence picking entry and exit points. Essential tool for any trader.',
  },
  {
    name: 'Pankaj C',
    since: 'Member Since March 2021',
    rating: 5,
    body: 'Thank you, InsiderFinance! You made a great product and I am looking forward to reaping the benefits.',
  },
  {
    name: 'Eddie M',
    since: 'Member Since March 2021',
    rating: 5,
    body: 'As a member, your communication is top notch, and it’s one of the things I actually appreciate from your service. In addition to that, I love getting data alerts via email.',
  },
];

const stats = [
  { id: 1, name: 'Traders interested in our tool', value: '105k+' },
  { id: 2, name: 'Traders benefited from our paid service', value: '6k+' },
  { id: 3, name: 'Traders learned from our free resources', value: '11k+' },
  { id: 4, name: 'Trade ideas discovered on our platform', value: '215k+' },
];

const landingQuote =
  'Trade ideas have a much higher chance of success when supported by strong technical analysis. Our proprietary system makes finding winning chart setups fast and easy so you can trade confidently every time.';

const tabs = [
  {
    label: 'Complete Technical Analysis System',
    desc: 'Includes buy/sell signals, dual oscillators, caution candles/dots, trend alerts, and risk management',
    content: (
      <div className="text-center">
        <img src="/images/overview-p-1080.png" className="rounded-md" />
        <p className="mx-auto max-w-2xl pt-3">
          Enter every trade with confidence knowing an elaborate series of indicators reinforces
          every buy/sell signal.
        </p>
      </div>
    ),
  },
  {
    label: 'Any Asset Class + Timeframe',
    desc: 'Accurately predict when to buy & sell stocks, options, forex, and futures with ease',
    content: (
      <div className="text-center">
        <p>
          <img src="/images/ta-any-asset-p-1080.png" className="rounded-md" />
          <p className="mx-auto max-w-2xl pt-3">
            The proprietary system adjusts itself for any symbol in any asset class with impressive
            precision to detect optimal entries and exits.
          </p>
        </p>
      </div>
    ),
  },
  {
    label: 'All Market Conditions',
    desc: 'Confidently trade any market direction: bull trends, bear trends, or sideways',
    content: (
      <div className="text-center">
        <img src="/images/technical-analysis-p-1080.png" className="rounded-md" />
        <div className="mx-auto max-w-2xl pt-3 text-center">
          <p className="">
            Institutional-grade trend prediction gives you an edge in any market condition: up,
            down, or sideways.
          </p>
        </div>
      </div>
    ),
  },
  {
    label: 'Built-In Confirmation + Alerts',
    desc: '4-Point confirmation system takes the guesswork out of timing perfect entries and exits',
    content: (
      <div className="text-center">
        <p>
          <img src="/images/signals.png" className="rounded-md" />
          <p className="mx-auto max-w-2xl pt-3">
            Clear confirmations and dual oscillators drastically reduce false signals, and alerts
            make it easy to find or monitor entries/exits.
          </p>
        </p>
      </div>
    ),
  },
];

const calloutHeading = 'Gain Your Effortless Edge With Automated Analysis';
const calloutSubheading =
  'Trade faster and more confidently with intuitive tools designed to increase your success';

const trackerCalloutHeading =
  'Sophisticated technical indicators are challenging to understand and implement. Our fully automated charting tools simplify complex analysis to help traders easily identify optimal entries/exits and trade confidently.';

const trackerStats = [
  {
    id: 1,
    name: 'Proprietary Signals',
    desc: 'Enter every trade confidently, backed by strong technical indicators in confluence.',
    logo: '/images/bb_signal-p-500.png',
  },
  {
    id: 2,
    name: 'Long Signal',
    desc: 'Buy signals when over 90% of our indicators in confluence predict a bull trend.',
    logo: '/images/bull-market-p-500.png',
  },
  {
    id: 3,
    name: 'Short Signal',
    desc: 'Sell signals when over 90% of our indicators in confluence predict a bear trend',
    logo: '/images/62ef9e621cef286586288791_bear-market-p-500.png',
  },
  {
    id: 4,
    name: 'Trend Change',
    desc: 'Trend change predicted with 60% of indicators in confluence.',
    logo: '/images/62ef9e70b7b092612b842c35_candlestick.png',
  },
];

const PRICING_TIER = [
  {
    name: 'Monthly',
    id: 'tier-monthly',
    href: TECHNICAL_ANALYSIS_SIGNUP_MONTHLY,
    price: 39,
    description: <div>Great value. $39 Billed Monthly.</div>,
    discountphrase: 'Less than the profit of a single trade',
    discount: 25,
    mostPopular: false,
    border: 'from-blue-500 to-blue-500',
  },
  {
    name: 'Quarterly',
    id: 'tier-quarterly',
    href: TECHNICAL_ANALYSIS_SIGNUP_QUARTERLY,
    price: 34,
    description: (
      <div>
        13% Discount. <span className="line-through">$117</span> $102 Billed Quarterly.
      </div>
    ),
    discount: 95,
    discountphrase: 'Save $60/Year',
    mostPopular: false,
    border: 'from-blue-500 to-blue-500',
  },
  {
    name: 'Annual',
    id: 'tier-annual',
    href: TECHNICAL_ANALYSIS_SIGNUP_YEARLY,
    price: 29,
    description: (
      <div>
        26% Discount. <span className="line-through">$468</span> $348 Billed Yearly.
      </div>
    ),
    discountphrase: 'Save $120/Year',
    discount: 150,
    mostPopular: true,
    border: 'from-blue-500 to-blue-500',
  },
];

const ROW_NAMES = [
  'Access To All InsiderFinance TradingView Scripts',
  'No TradingView Subscription Required',
  'Accurate Entries And Exits',
  'Create Breakout Signals',
  'Squeeze Indicator',
  'Momentum Indicator',
  'Automated Risk Management',
  'Caution Candles + Dots',
  'Trend Confirmation Line',
  'Reversal Diamonds',
  'Moving Average Clouds',
  'Real-Time News + Sentiment',
  'Bollinger Bands',
  'Full Discord Access',
  'BONUS: Real-Time Trade Ideas',
];

const TechnicalAnalysis: NextPage = () => {
  return (
    <>
      <Head
        title="Technical Analysis"
        description="Increase Your Win Rate With #1 TradingView Indicators + All-Inclusive Education"
        useCurrentUrl
      />
      <PageLayout hasPadTop>
        <TopText heading="Increase Your Win Rate With #1 TradingView Indicators + All-Inclusive Education" />
        <LandingHeroSection
          title="Powerful Technical Analysis. Automated & Simplified."
          description="Accurate entries and exits without the heavy lifting"
          heroImageUrl="/images/hero-ta.png"
          buyNowButtonText="Get Started"
          buyNowUrl={`#${PRICING_SECTION}`}
        />
        <StatsSection stats={stats} />
        <FeaturedSection />
        <AwardBlock />
        <LandingQuote heading={landingQuote} />
        <CallOutSection tabsItems={tabs} heading={calloutHeading} subheading={calloutSubheading} />
        <TopBlock />
        <TradingSystemTabs />
        <TrackerItems
          statsItem={trackerStats}
          calloutHeading={trackerCalloutHeading}
          heading="Create Breakout Signals On Any Ticker & Time Frame"
        />
        <Testimonials testimonials={testimonials} />
        <PricingCardsComponent
          topText="LAUNCH PRICING + BONUS GIFT"
          heading="Sign up today and receive our limited-time technical analysis indicator launch price + FREE trade idea alerts 3 times per day"
          pricingTiers={PRICING_TIER}
          leadingText="Technical analysis without the dashboard."
        />
        <PricingGrid rowItems={ROW_NAMES} />
        <Discord
          heading="FREE LAUNCH GIFT: Market Analysis & Trade Ideas"
          subheading="Real-Time Market Analysis & Trade Ideas"
          desc="Receive real-time snapshots of the InsiderFinance dashboard delivered through Discord or email so you never miss big moves. Download examples below."
        />
        <EmailVisuals />
        <CallOutHeadingSection signup={false} />
      </PageLayout>
    </>
  );
};

export default TechnicalAnalysis;
